/**
 * Tenants Management Page
 */

import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import type { Tenant, CreateTenantInput } from '../api/types';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Modal, ConfirmModal } from '../components/Modal';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  EmptyState,
} from '../components/Table';
import { Plus, Trash2, Edit2, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

export function TenantsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.listTenants();
      setTenants(res.tenants);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTenant = async (input: CreateTenantInput) => {
    try {
      await apiClient.createTenant(input);
      setIsCreateOpen(false);
      loadTenants();
    } catch (err) {
      console.error('Failed to create tenant:', err);
      throw err;
    }
  };

  const handleUpdateTenant = async (input: { slug?: string; name?: string; description?: string | null }) => {
    if (!selectedTenant) return;
    try {
      await apiClient.updateTenant(selectedTenant.uuid, input);
      setIsEditOpen(false);
      setSelectedTenant(null);
      loadTenants();
    } catch (err) {
      console.error('Failed to update tenant:', err);
      throw err;
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    try {
      await apiClient.deleteTenant(selectedTenant.uuid);
      setIsDeleteOpen(false);
      setSelectedTenant(null);
      loadTenants();
    } catch (err) {
      console.error('Failed to delete tenant:', err);
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-500 mt-1">
            Manage tenants for multi-tenant isolation
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={loadTenants}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4" />
            Add Tenant
          </Button>
        </div>
      </div>

      {/* Tenants Table */}
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Slug</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell align="right">Actions</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  Loading...
                </td>
              </tr>
            ) : tenants.length === 0 ? (
              <EmptyState message="No tenants found. Add a tenant to get started." colSpan={5} />
            ) : (
              tenants.map((tenant) => (
                <TableRow key={tenant.uuid}>
                  <TableCell>
                    <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                      {tenant.slug}
                    </code>
                  </TableCell>
                  <TableCell className="font-medium">{tenant.name}</TableCell>
                  <TableCell>{tenant.description || '-'}</TableCell>
                  <TableCell>
                    {format(new Date(tenant.createdAt), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell align="right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          setIsEditOpen(true);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          setIsDeleteOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Tenant Modal */}
      <CreateTenantModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreateTenant}
      />

      {/* Edit Tenant Modal */}
      {selectedTenant && (
        <EditTenantModal
          isOpen={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setSelectedTenant(null);
          }}
          tenant={selectedTenant}
          onSave={handleUpdateTenant}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedTenant(null);
        }}
        onConfirm={handleDeleteTenant}
        title="Delete Tenant"
        message={`Are you sure you want to delete "${selectedTenant?.name}"? This will also delete all users and API keys in this tenant.`}
        confirmText="Delete Tenant"
        variant="danger"
      />
    </div>
  );
}

// Create Tenant Form Modal
function CreateTenantModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateTenantInput) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateTenantInput>({
    slug: '',
    name: '',
    description: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await onCreate(form);
      setForm({
        slug: '',
        name: '',
        description: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    setForm({ ...form, name, slug });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Tenant" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g., Acme Corporation"
          required
        />
        <Input
          label="Slug"
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
          placeholder="e.g., acme-corp"
          helperText="URL-friendly identifier (lowercase, hyphens only)"
          required
        />
        <Input
          label="Description (optional)"
          value={form.description || ''}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="e.g., Production tenant for Acme"
        />

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Add Tenant
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit Tenant Form Modal
function EditTenantModal({
  isOpen,
  onClose,
  tenant,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  tenant: Tenant;
  onSave: (input: { slug?: string; name?: string; description?: string | null }) => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    slug: tenant.slug,
    name: tenant.name,
    description: tenant.description || '',
  });

  // Reset form when tenant changes
  useEffect(() => {
    setForm({
      slug: tenant.slug,
      name: tenant.name,
      description: tenant.description || '',
    });
  }, [tenant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await onSave({
        slug: form.slug !== tenant.slug ? form.slug : undefined,
        name: form.name !== tenant.name ? form.name : undefined,
        description: form.description !== (tenant.description || '') ? (form.description || null) : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Tenant" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g., Acme Corporation"
          required
        />
        <Input
          label="Slug"
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
          placeholder="e.g., acme-corp"
          helperText="Changing the slug may break existing references"
          required
        />
        <Input
          label="Description (optional)"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="e.g., Production tenant for Acme"
        />

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}
